import axios from "axios";
import {
  EMAIL_CHANGE_COMPLETE,
  EMAIL_CHANGE_OTP,
  EMAIL_CHANGE_START,
  EMAIL_CHANGE_STATE
} from "../../../utils/url";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

/**
 * Support email change (account recovery) — stage 1.
 *
 * Every call here must carry the signed-in support agent's own JWT: these are
 * account-recovery actions and the backend attributes each one to the agent on
 * the token. The shared x-api-key-value key passes the authorizer but has no
 * agent identity, so it earns a flat 403 UNAUTHORIZED.
 */
const authHeaders = () => ({
  Authorization: `bearer ${localStorage.getItem("accessToken")}`,
  "Content-Type": "application/json"
});

export type EmailChangeAction =
  | "SendMobileOtp"
  | "SendEmailOtp"
  | "VerifyMobileOtp"
  | "VerifyEmailOtp";

/**
 * One pre-flight check the backend ran when opening the recovery. The wording
 * is the backend's — render `Text` verbatim and never map `Code` to a label of
 * our own. `Status` is "Pass" or "Attention"; Attention is a warning to show
 * the agent, NOT a failure (the recovery opened either way).
 */
export interface PreCheck {
  Code: string;
  Status: string;
  Text: string;
}

export const isPreCheckPass = (check: PreCheck) =>
  /^pass$/i.test((check?.Status || "").trim());

export interface StartRecoveryResponse {
  RecoverySessionId: string;
  NewEmail?: string;
  NewEmailMasked?: string;
  /** the full number on the account — not masked */
  MobileOnAccount?: string;
  /** transitional duplicate of MobileOnAccount; being removed by the backend */
  MaskedMobile?: string;
  Superseded?: boolean;
  Existing?: boolean;
  MobileVerified?: boolean;
  EmailVerified?: boolean;
  PreChecks?: PreCheck[];
}

export interface OtpResponse {
  RecoverySessionId: string;
  MobileVerified: boolean;
  EmailVerified: boolean;
}

/**
 * One row of the recovery history. The GET returns a bare array — the live
 * recovery first (if there is one), older attempts after it — so `Status` is
 * what separates the one the agent can act on from the ones that are only
 * there for context.
 */
export interface RecoverySessionRecord {
  Status: string;
  RecoverySessionId: string;
  NewEmail?: string;
  NewEmailMasked?: string;
  /** not sent by the current build — the table falls back when it is absent */
  OldEmail?: string;
  OldEmailMasked?: string;
  /** who closed the recovery out; falls back to RequestedBy */
  ProcessedBy?: string;
  /** when it reached its final state; falls back to LastActivityAt */
  ProcessedAt?: string;
  MobileOnAccount?: string;
  MaskedMobile?: string;
  MobileVerified: boolean;
  EmailVerified: boolean;
  CreatedAt?: string;
  LastActivityAt?: string;
  ExpiresAt?: string;
  RequestedBy?: string;
}

/** Only an Active row can be sent to, verified against, or completed. */
export const isActiveRecovery = (record: RecoverySessionRecord) =>
  /^active$/i.test((record.Status || "").trim());

/**
 * Normalised failure. The backend speaks two dialects:
 *  - flat bodies (403 / 404 / 429) carry `ErrorCode`
 *  - 409s carry `Failures[0].Property` + `FailureType`
 * `message` is always the backend's own wording — it is written to be read out
 * to the agent verbatim, so it is displayed and never parsed.
 */
export interface EmailChangeError {
  status: number | null;
  errorCode: string | null;
  property: string | null;
  failureType: string | null;
  message: string;
  retryAfterSeconds: number | null;
}

const FALLBACK_MESSAGE = "Something went wrong. Please try again.";

export const parseEmailChangeError = (err: any): EmailChangeError => {
  const response = err?.response;
  const status: number | null = response?.status ?? null;
  const body = response?.data;

  if (!response) {
    return {
      status: null,
      errorCode: "NETWORK_ERROR",
      property: null,
      failureType: null,
      message: "Could not reach the server. Check your connection and retry.",
      retryAfterSeconds: null
    };
  }

  const failure = Array.isArray(body?.Failures) ? body.Failures[0] : null;

  const headerRetryAfter = Number(response?.headers?.["retry-after"]);
  const retryAfterSeconds =
    Number(body?.RetryAfterSeconds) ||
    (Number.isFinite(headerRetryAfter) ? headerRetryAfter : 0) ||
    null;

  return {
    status,
    errorCode: body?.ErrorCode ?? null,
    property: failure?.Property ?? null,
    failureType: failure?.FailureType ?? null,
    message: failure?.Message || body?.Message || FALLBACK_MESSAGE,
    retryAfterSeconds
  };
};

/** Open a recovery (or return the one already open) for this account. */
export const startEmailChange = async (
  userId: string,
  newEmail: string
): Promise<StartRecoveryResponse> => {
  const { data } = await axios.post(
    `${apiBaseUrl}${EMAIL_CHANGE_START}`,
    { UserId: userId, NewEmail: newEmail },
    { headers: authHeaders() }
  );
  return data;
};

/**
 * Send or verify one of the two proof codes. The new address is pinned to the
 * session at start — it cannot be changed or redirected from here.
 */
export const postEmailChangeOtp = async (
  recoverySessionId: string,
  action: EmailChangeAction,
  otp?: string
): Promise<OtpResponse> => {
  const body: Record<string, string> = {
    RecoverySessionId: recoverySessionId,
    Action: action
  };
  if (otp) body.OTP = otp;

  const { data } = await axios.post(`${apiBaseUrl}${EMAIL_CHANGE_OTP}`, body, {
    headers: authHeaders()
  });
  return data;
};

/**
 * The ordered work the backend does to move the address across. Named here so
 * a partial result renders in the order the steps actually ran, rather than in
 * whatever order the object happens to enumerate.
 */
export const COMPLETE_STEP_ORDER = [
  "Firebase",
  "Unlink",
  "Revoke",
  "Record",
  "Event",
  "Notices"
] as const;

export const COMPLETE_STEP_LABELS: Record<string, string> = {
  Firebase: "Firebase sign-in record",
  Unlink: "Unlink old identity",
  Revoke: "Revoke sessions",
  Record: "Customer record",
  Event: "Event published",
  Notices: "Notification emails"
};

export interface CompleteEmailChangeResponse {
  Status: string;
  Steps: Record<string, string>;
  RecoveryAuditId: string;
}

/**
 * A step outcome is one of Done / Pending / "Failed: <reason>". Anything that
 * is not literally Done is treated as not done.
 */
export const isStepDone = (value: string) =>
  /^done$/i.test((value || "").trim());
export const isStepFailed = (value: string) =>
  /^failed/i.test((value || "").trim());

/**
 * The change ran end to end. Note that a partial run also answers 200 — with
 * Status "Incomplete" and the failing step named in Steps — so the HTTP status
 * alone must never be read as success.
 */
export const isCompleteSuccess = (data: CompleteEmailChangeResponse) =>
  /^completed$/i.test((data?.Status || "").trim());

/**
 * Apply the address change once both proofs are recorded.
 *
 * Every request/validation failure comes back as 409 with the Failures
 * envelope — including session unknown/expired and already-completed, which
 * are 409 here rather than the 404 the other endpoints use.
 */
export const completeEmailChange = async (
  recoverySessionId: string
): Promise<CompleteEmailChangeResponse> => {
  const { data } = await axios.post(
    `${apiBaseUrl}${EMAIL_CHANGE_COMPLETE}`,
    { RecoverySessionId: recoverySessionId },
    { headers: authHeaders() }
  );
  return data;
};

/**
 * Read every recovery recorded against an account, newest first.
 *
 * An empty array means nothing was ever started — a normal state, not a fault.
 * A 404 is treated the same way for safety, since older builds of the endpoint
 * answered FLOW_SESSION_NOT_FOUND instead of returning `[]`.
 */
export const getEmailChangeSessions = async (
  userId: string
): Promise<RecoverySessionRecord[]> => {
  try {
    const { data } = await axios.get(
      `${apiBaseUrl}${EMAIL_CHANGE_STATE(userId)}`,
      { headers: authHeaders() }
    );
    if (Array.isArray(data)) return data;
    // Defensive: tolerate a single object if the shape ever regresses.
    return data?.RecoverySessionId ? [data] : [];
  } catch (err: any) {
    if (err?.response?.status === 404) return [];
    throw err;
  }
};
